import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId) || articles[0];

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '禁漫天堂',
    },
    publisher: {
      '@type': 'Organization',
      name: '禁漫天堂',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.excerpt,
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryInfo?.name || '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                {categoryInfo && (
                  <Link
                    to={`/category/${categoryInfo.slug}`}
                    className="px-2.5 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap hover:bg-secondary-200 transition-colors"
                  >
                    {categoryInfo.name}
                  </Link>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间约 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  禁漫天堂编辑部
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full h-48 md:h-64 rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20manga%20style%20illustration%20with%20warm%20crimson%20tones%2C%20soft%20atmospheric%20lighting%2C%20abstract%20comic%20elements%2C%20clean%20minimalist%20composition%2C%20editorial%20photography%20style%2C%20no%20text&width=1200&height=500&seq=article-featured-main&orientation=landscape"
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-foreground-500">分享文章：</span>
                  {[
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-wechat-line', label: '微信' },
                    { icon: 'ri-qq-line', label: 'QQ' },
                  ].map((platform) => (
                    <button
                      key={platform.label}
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors"
                      aria-label={`分享到${platform.label}`}
                    >
                      <i className={platform.icon}></i>
                    </button>
                  ))}
                </div>
                <Link
                  to={`/category/${article.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
                >
                  返回分类列表
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group block p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all"
                  >
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                      {rel.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{rel.date}</span>
                      <span>{rel.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}